import { IconComponentProps } from '@nexelem/icons';
import { ProLightCircleExclamation } from '@nexelem/icons/ProLightCircleExclamation';
import { SelectField, SelectFieldNonFormProps, SelectFieldProps } from '@nexelem/inputs';
import { FC, ReactNode, useMemo } from 'react';
import { Control, FieldValues, Path, useController, get } from 'react-hook-form';
import { MaxWidthProp, maxWidthVariants } from '../utils/maxWidthVariants';
import { cn } from '@nexelem/utils';

export type FormSelectInputNonFormProps = {
  maxWidth?: MaxWidthProp;
  label?: string | ReactNode;
  placeholder?: string;
  errorPath?: string;
  required?: boolean;
  showErrorMessage?: boolean;
  HelperTextIcon?: FC<IconComponentProps>;
  showHelperTextIcon?: boolean;
  isReadOnly?: boolean;
  customErrorMessage?: string;
} & Omit<SelectFieldNonFormProps, 'width'>;

export type FormSelectInputProps<T, Value extends FieldValues, InitValueType = T> = Omit<
  SelectFieldProps<T>,
  'onChange' | 'width'
> & {
  name: Path<Value>;
  control?: Control<Value>;
  getInitValue?: (value: InitValueType) => T | undefined;
  onChange?: SelectFieldProps<T>['onChange'];
} & FormSelectInputNonFormProps;

export const FormSelectInput = <T, Value extends FieldValues, InitValueType = T>({
  maxWidth = 320,
  label,
  placeholder,
  getInitValue,
  errorPath,
  onChange: onChangeProp,
  showErrorMessage = true,
  showHelperTextIcon = true,
  HelperTextIcon = ProLightCircleExclamation,
  helperText,
  name,
  customErrorMessage,
  isMultiple,
  className,
  disabled,
  ...props
}: FormSelectInputProps<T, Value, InitValueType>) => {
  const { field, formState } = useController({ ...props, name });

  // This memo is crucial because for isMultiple, we use array as value
  // and below if the value is not there yet, we create new array every render (below) for initial value
  // Autocomplete compares prevValue and value and if they are not same (aka. something was selected), it resets input
  const value = useMemo(
    () => getInitValue?.(field.value) ?? field.value ?? (isMultiple ? [] : null),
    [field.value, isMultiple, getInitValue],
  );

  const handleChange = (value: T | NonNullable<T> | T[] | null) => {
    field.onChange(value);
    onChangeProp?.(value);
  };

  const errorMessage = showErrorMessage ? (get(formState.errors, errorPath ?? `${name}.message`) as string) : undefined;

  const selectedHelperTextIcon = useMemo(() => {
    if (get(formState.errors, name)) {
      return ProLightCircleExclamation;
    } else {
      return showHelperTextIcon ? HelperTextIcon : undefined;
    }
  }, [HelperTextIcon, formState.errors, name, showHelperTextIcon]);

  const hasError = !!get(formState.errors, name);

  return (
    <SelectField
      {...field}
      {...props}
      className={cn(maxWidthVariants({ maxWidth }), className)}
      disabled={disabled}
      error={hasError}
      errorMessage={errorMessage || customErrorMessage}
      helperText={helperText}
      HelperTextIcon={selectedHelperTextIcon}
      isMultiple={isMultiple}
      label={label}
      placeholder={placeholder}
      showErrorBorder={hasError}
      value={value}
      onChange={handleChange}
    />
  );
};
